// src/features/settings/components/scanners-panel/ScannerFormDialog.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

const EMPTY = { name: '', password: '', default_location_id: '', require_staff_login: false };
const MIN_PASSWORD = 4;

export default function ScannerFormDialog({ open, scanner, locations, saving, onClose, onSave }) {
  const { t } = useTranslation(['settings', 'common']);
  const isEdit = Boolean(scanner?.scanner_id);
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setValues(isEdit ? {
      name: scanner.name,
      password: '',
      default_location_id: scanner.default_location_id || '',
      require_staff_login: scanner.require_staff_login,
    } : EMPTY);
    setErrors({});
    setSubmitError(null);
  }, [open, isEdit, scanner]);

  const set = (field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!values.name.trim()) next.name = t('settings:scanners.validation.name_required');
    if (!isEdit && !values.password) next.password = t('settings:scanners.validation.password_required');
    if (values.password && values.password.length < MIN_PASSWORD) {
      next.password = t('settings:scanners.validation.password_short', { count: MIN_PASSWORD });
    }
    if (Object.keys(next).length) { setErrors(next); return; }
    setSubmitError(await onSave(values));
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>
          {isEdit ? t('settings:scanners.dialog.edit_title') : t('settings:scanners.dialog.create_title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <TextField
              label={t('settings:scanners.form.name')}
              helperText={errors.name || t('settings:scanners.form.name_help')}
              error={Boolean(errors.name)}
              value={values.name}
              onChange={e => set('name', e.target.value)}
              required
              autoFocus
              fullWidth
            />
            <TextField
              label={t('settings:scanners.form.password')}
              helperText={errors.password || t(isEdit ? 'settings:scanners.form.password_help_edit' : 'settings:scanners.form.password_help')}
              error={Boolean(errors.password)}
              value={values.password}
              onChange={e => set('password', e.target.value)}
              required={!isEdit}
              autoComplete="new-password"
              fullWidth
            />
            <TextField
              select
              label={t('settings:scanners.form.default_location')}
              helperText={t('settings:scanners.form.default_location_help')}
              value={values.default_location_id}
              onChange={e => set('default_location_id', e.target.value)}
              fullWidth
            >
              <MenuItem value="">
                <em>{t('settings:scanners.form.no_default_location')}</em>
              </MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc.stock_location_id} value={loc.stock_location_id}>{loc.name}</MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={values.require_staff_login}
                  onChange={e => set('require_staff_login', e.target.checked)}
                />
              }
              label={
                <>
                  <Typography variant="body2">{t('settings:scanners.form.require_staff_login')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('settings:scanners.form.require_staff_login_help')}
                  </Typography>
                </>
              }
              sx={{ alignItems: 'flex-start', '& .MuiSwitch-root': { mt: -0.5 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>{t('common:buttons.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {isEdit ? t('common:buttons.save') : t('common:buttons.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
